import React, { useState, useEffect, useCallback } from 'react';
import authService from '../../services/authService';
import API_CONFIG from '../../config/api';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const PLATFORM_LABELS = {
  link: '링크 복사',
  chat: '채팅 공유',
  twitter: 'Twitter',
  facebook: 'Facebook',
  kakao: 'KakaoTalk',
};

function StatCard({ icon, label, value, sub }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      padding: '14px 18px', flex: '1 1 110px', minWidth: 110,
      background: 'var(--color-monotone-1)', borderRadius: 'var(--radius-md, 8px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-monotone-4)', fontSize: 12 }}>
        <span className="material-symbols-rounded" style={{ fontSize: 15 }}>{icon}</span>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--color-monotone-4)' }}>{sub}</div>}
    </div>
  );
}

function BarChart({ data, labelFn }) {
  if (!data || data.length === 0) {
    return <p style={{ fontSize: 13, color: 'var(--color-monotone-4)', textAlign: 'center', padding: '8px 0' }}>데이터가 없습니다.</p>;
  }
  const maxVal = Math.max(...data.map((d) => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 72 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div
            style={{
              width: '100%',
              height: Math.max(3, (d.count / maxVal) * 56),
              background: 'var(--color-primary)',
              borderRadius: 3,
              opacity: d.count === 0 ? 0.15 : 1,
            }}
            title={`${labelFn(d)}: ${d.count}회`}
          />
          <span style={{ fontSize: 10, color: 'var(--color-monotone-4)', whiteSpace: 'nowrap' }}>
            {labelFn(d)}
          </span>
        </div>
      ))}
    </div>
  );
}

function PostStatsPanel({ postId, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!postId) return;
    setLoading(true);
    setError(null);
    try {
      const token = authService.getUserData()?.token;
      const res = await fetch(`/api/post/${postId}/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || '통계를 불러오지 못했습니다.');
      setStats(data.stats);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ESC 닫기
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const shareBreakdown = stats
    ? Object.entries(stats.sharesByPlatform || {})
        .sort((a, b) => b[1] - a[1])
        .map(([p, cnt]) => `${PLATFORM_LABELS[p] ?? p} ${cnt}`)
        .join(' · ')
    : null;

  const readRatio = stats && stats.views > 0
    ? Math.round((stats.uniqueViews / stats.views) * 100)
    : null;

  const hourData = stats
    ? Array.from({ length: 24 }, (_, h) => {
        const found = stats.viewsByHour.find((r) => r.hour === h);
        return { hour: h, count: found?.count ?? 0 };
      })
    : [];

  const dayData = stats
    ? Array.from({ length: 7 }, (_, d) => {
        const found = stats.viewsByDay.find((r) => r.day === d);
        return { day: d, count: found?.count ?? 0 };
      })
    : [];

  return (
    <>
      {/* 오버레이 */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 1000,
        }}
      />

      {/* 팝업 */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(600px, 95vw)',
        maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        background: 'var(--color-surface, #fff)',
        borderRadius: 'var(--radius-lg, 12px)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
        zIndex: 1001,
        overflow: 'hidden',
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--color-monotone-2)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 16 }}>
            <span className="material-symbols-rounded" style={{ fontSize: 20 }}>bar_chart</span>
            통계
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn-sqr btn-monotone-1"
              onClick={fetchStats}
              disabled={loading}
              title="새로고침"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16 }}>refresh</span>
            </button>
            <button className="btn-sqr btn-monotone-1" onClick={onClose} title="닫기">
              <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
            </button>
          </div>
        </div>

        {/* 바디 (스크롤) */}
        <div style={{ overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {loading && (
            <p style={{ textAlign: 'center', color: 'var(--color-monotone-4)', padding: '40px 0' }}>불러오는 중...</p>
          )}
          {error && (
            <p style={{ color: 'var(--color-failure)', textAlign: 'center' }}>{error}</p>
          )}

          {stats && !loading && (
            <>
              {/* 핵심 지표 */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <StatCard icon="web_traffic" label="총 조회수" value={stats.views.toLocaleString()} sub={`유니크 ${stats.uniqueViews.toLocaleString()}명`} />
                <StatCard icon="favorite" label="좋아요" value={stats.likes.toLocaleString()} />
                <StatCard icon="share" label="공유" value={stats.shares.toLocaleString()} sub={shareBreakdown || undefined} />
                <StatCard icon="bookmark" label="저장" value={stats.saves.toLocaleString()} />
                <StatCard icon="comment" label="댓글" value={stats.comments.toLocaleString()} />
                {readRatio !== null && (
                  <StatCard icon="person" label="유니크 비율" value={`${readRatio}%`} sub="유니크÷총 조회수" />
                )}
              </div>

              {/* 좋아요 누른 사람 */}
              <div>
                <h5 style={{ marginBottom: 10 }}>좋아요 누른 사람 ({stats.likes})</h5>
                {stats.likers && stats.likers.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {stats.likers.map((u) => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }} title={u.nickname}>
                        <img
                          src={u.profile_image ? API_CONFIG.resolveFileUrl(u.profile_image) : API_CONFIG.getDefaultAvatar(u.id)}
                          className="img-profile-comment"
                          alt={u.nickname}
                          onError={(e) => { e.target.onerror = null; e.target.src = API_CONFIG.getDefaultAvatar(u.id); }}
                          style={{ flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 13 }}>{u.nickname}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--color-monotone-4)' }}>아직 좋아요가 없습니다.</p>
                )}
              </div>

              {/* 시간대별 차트 */}
              <div>
                <h5 style={{ marginBottom: 10 }}>
                  시간대별 조회
                  <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--color-monotone-4)', marginLeft: 6 }}>최근 30일</span>
                </h5>
                <BarChart data={hourData} labelFn={(d) => `${d.hour}시`} />
              </div>

              {/* 요일별 차트 */}
              <div>
                <h5 style={{ marginBottom: 10 }}>
                  요일별 조회
                  <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--color-monotone-4)', marginLeft: 6 }}>최근 30일</span>
                </h5>
                <BarChart data={dayData} labelFn={(d) => DAY_LABELS[d.day]} />
              </div>

              {/* 플랫폼별 공유 */}
              {Object.keys(stats.sharesByPlatform || {}).length > 0 && (
                <div>
                  <h5 style={{ marginBottom: 10 }}>공유 경로</h5>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {Object.entries(stats.sharesByPlatform).map(([platform, cnt]) => (
                      <div key={platform} className="btn-rtg btn-monotone-1" style={{ cursor: 'default' }}>
                        {PLATFORM_LABELS[platform] ?? platform}: {cnt}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default PostStatsPanel;
